import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  useClearRiskRankOverride,
  useRecomputeRiskRank,
  useRiskRankBoard,
  useRiskRankCandidates,
  useRiskRankConfig,
  useSetRiskRankOverride,
} from '../api/hooks';
import { useAuth } from '../auth/AuthContext';
import './RiskRankPage.css';

const MARKET_LABELS = {
  player_touchdowns: 'Anytime TD',
  player_passing_touchdowns: 'Passing TDs',
  player_fantasy_points: 'Fantasy Pts',
  player_field_goals_made: 'FG Made',
};

function marketLabel(marketId) {
  if (MARKET_LABELS[marketId]) return MARKET_LABELS[marketId];
  return String(marketId || '')
    .replace(/^player_/, '')
    .split('_')
    .map((w) => (w === '+' ? '+' : w.charAt(0).toUpperCase() + w.slice(1)))
    .join(' ');
}

function formatApiError(err) {
  const d = err?.response?.data;
  if (typeof d?.detail === 'string') return d.detail;
  if (Array.isArray(d?.detail)) return d.detail.map((x) => x.msg || JSON.stringify(x)).join('; ');
  return err?.message || 'Request failed';
}

function kickoff(iso) {
  if (!iso) return '';
  const d = new Date(iso.endsWith('Z') || iso.includes('+') ? iso : `${iso}Z`);
  return d.toLocaleString(undefined, { weekday: 'short', hour: 'numeric', minute: '2-digit' });
}

function ago(iso) {
  if (!iso) return null;
  const s = Math.max(0, (Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return 'just now';
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  return `${Math.floor(s / 3600)}h ago`;
}

function matchup(f) {
  if (!f) return '';
  return f.home_team && f.away_team ? `${f.away_team} @ ${f.home_team}` : f.fixture_id;
}

/** Score with the player digits and the two market digits set apart, e.g. 0.998|99. */
function Score({ value, scopeType }) {
  if (value === null || value === undefined) return <span className="rr-score rr-score-none">—</span>;
  const digits = scopeType === 'league' ? 5 : 4;
  const s = Number(value).toFixed(digits);
  return (
    <span className="rr-score" title="Leading digits: player rank · last two digits: market rank">
      <span className="rr-score-player">{s.slice(0, -2)}</span>
      <span className="rr-score-market">{s.slice(-2)}</span>
    </span>
  );
}

/** Small popover anchored to its trigger: pick a slot (pin) or confirm an exclude. */
function OverridePopover({ mode, topN, autoFill, onSubmit, onClose, busy }) {
  const [slot, setSlot] = useState(null);
  const [reason, setReason] = useState('');
  const ref = useRef(null);
  useEffect(() => {
    const onDoc = (e) => ref.current && !ref.current.contains(e.target) && onClose();
    const onKey = (e) => e.key === 'Escape' && onClose();
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [onClose]);
  const pin = mode === 'pin';
  return (
    <div className={`rr-pop ${pin ? 'rr-pop-pin' : 'rr-pop-excl'}`} ref={ref} role="dialog">
      <div className="rr-pop-title">{pin ? 'Pin to Featured slot' : 'Exclude this market'}</div>
      <p className="rr-pop-help">
        {!pin
          ? 'Never featured in this scope. The player’s other markets close the gap: approved first, then the rest, fewest over entries first.'
          : autoFill
            ? 'The player holds this slot through every recompute, with this market first.'
            : 'Features this market in this slot until it is unpinned or the game ends.'}
      </p>
      {pin && (
        <div className="rr-slots">
          {Array.from({ length: topN }, (_, i) => i + 1).map((n) => (
            <button
              key={n}
              type="button"
              className={`rr-slot ${slot === n ? 'is-on' : ''}`}
              onClick={() => setSlot(n)}
            >
              {n}
            </button>
          ))}
        </div>
      )}
      <input
        className="rr-input"
        placeholder="Reason (optional)"
        value={reason}
        onChange={(e) => setReason(e.target.value)}
      />
      <div className="rr-pop-actions">
        <button type="button" className="rr-btn rr-btn-ghost" onClick={onClose}>
          Cancel
        </button>
        <button
          type="button"
          className={`rr-btn ${pin ? 'rr-btn-amber' : 'rr-btn-red'}`}
          disabled={busy || (pin && !slot)}
          onClick={() => onSubmit(pin ? { pin_slot: slot } : { excluded: true }, reason)}
        >
          {pin ? (slot ? `Pin to #${slot}` : 'Pick a slot') : 'Exclude'}
        </button>
      </div>
    </div>
  );
}

function MarketActions({ marketKey, override, topN, autoFill, busy, onSet, onClear }) {
  const [open, setOpen] = useState(null);
  if (override) {
    return (
      <button type="button" className="rr-btn rr-btn-ghost rr-btn-sm" disabled={busy} onClick={() => onClear(override.id)}>
        {override.excluded ? 'Restore' : 'Unpin'}
      </button>
    );
  }
  return (
    <span className="rr-actions">
      <button type="button" className="rr-btn rr-btn-sm rr-btn-amber-ghost" onClick={() => setOpen('pin')}>
        Pin
      </button>
      {autoFill && (
        <button type="button" className="rr-btn rr-btn-sm rr-btn-red-ghost" onClick={() => setOpen('exclude')}>
          Exclude
        </button>
      )}
      {open && (
        <OverridePopover
          mode={open}
          topN={topN}
          autoFill={autoFill}
          busy={busy}
          onClose={() => setOpen(null)}
          onSubmit={async (value, reason) => {
            const ok = await onSet(marketKey, value, reason);
            if (ok) setOpen(null);
          }}
        />
      )}
    </span>
  );
}

const UNRANKED = {
  no_slot: { label: 'No slot', cls: 'rr-tag-gray', hint: 'The score holds 100 markets per player; this one is past that. Pin it to move it up.' },
  not_quoted: { label: 'Not quoted', cls: 'rr-tag-gray', hint: 'Suspended or closed right now, so it cannot rank.' },
  excluded: { label: 'Excluded', cls: 'rr-tag-red', hint: 'Excluded by a trader in this scope.' },
};

const COLLAPSE_KEY = 'riskRank.collapsedByDefault';

function readCollapsedDefault() {
  try {
    return window.localStorage.getItem(COLLAPSE_KEY) !== '0';
  } catch {
    return true;
  }
}

function writeCollapsedDefault(value) {
  try {
    window.localStorage.setItem(COLLAPSE_KEY, value ? '1' : '0');
  } catch {
    /* per-viewer convenience only */
  }
}

function PlayerRow({ player, scopeType, fixturesById, overrideByMarket, topN, autoFill, busy, onSet, onClear, collapsed, onToggle }) {
  const [showUnranked, setShowUnranked] = useState(false);
  const ranked = player.markets.filter((m) => m.score !== null && m.score !== undefined);
  const unranked = player.markets.filter((m) => m.score === null || m.score === undefined);
  const first = ranked[0] || player.markets[0];
  const pinned = ranked.some((m) => m.is_pinned);
  const maxOvers = Math.max(1, ...player.markets.map((m) => m.features?.market_over_bets || 0));
  const natural = first.features?.natural_player_rank;
  return (
    <div className={`rr-player ${pinned ? 'is-pinned' : ''} ${collapsed ? 'is-collapsed' : ''}`}>
      <button
        type="button"
        className="rr-collapse"
        onClick={onToggle}
        aria-expanded={!collapsed}
        aria-label={collapsed ? 'Expand player' : 'Collapse player'}
      >
        <span className="rr-chevron" />
      </button>
      <div className="rr-rank">
        <span className="rr-rank-n">{player.rank}</span>
        {pinned ? (
          <span className="rr-tag rr-tag-amber">Pinned</span>
        ) : (
          natural && natural !== player.rank && <span className="rr-rank-nat">natural #{natural}</span>
        )}
      </div>
      <div className="rr-player-info">
        <div className="rr-player-name">{first.player_name || first.player_key}</div>
        <div className="rr-player-meta">
          {first.position && <span className="rr-pos">{first.position}</span>}
          {scopeType === 'league' && <span>{matchup(fixturesById[first.fixture_id])}</span>}
          <span className="rr-dim">{(first.features?.player_total_bets ?? 0).toLocaleString()} entries</span>
        </div>
      </div>
      {collapsed ? (
        <button type="button" className="rr-summary" onClick={onToggle}>
          <Score value={first.score} scopeType={scopeType} />
          <span className="rr-market-name">
            {marketLabel(first.market_id)}
            {first.is_pinned && <span className="rr-tag rr-tag-amber">Pinned</span>}
          </span>
          <span className="rr-dim">
            {ranked.length} ranked{unranked.length ? ` · ${unranked.length} unranked` : ''}
          </span>
        </button>
      ) : (
      <div className="rr-markets">
        {ranked.map((m) => {
          const overs = m.features?.market_over_bets || 0;
          return (
            <div key={m.market_key} className={`rr-market ${m.is_pinned ? 'is-pinned' : ''} ${m.market_rank === 1 ? 'is-lead' : ''}`}>
              <Score value={m.score} scopeType={scopeType} />
              <span className="rr-market-name">
                {marketLabel(m.market_id)}
                {m.market_rank === 1 && <span className="rr-tag rr-tag-blue">Lead</span>}
                {m.features?.approved === false && (
                  <span className="rr-tag rr-tag-gray" title="Not on the approved list for this position, so it ranks after the approved ones">
                    Unapproved
                  </span>
                )}
              </span>
              <span className="rr-overs" title={`${m.features?.market_bets ?? 0} entries, ${overs} on the over`}>
                <span className="rr-overs-bar">
                  <span style={{ width: `${(overs / maxOvers) * 100}%` }} />
                </span>
                <span className="rr-dim">{overs} over</span>
              </span>
              <MarketActions
                marketKey={m.market_key}
                override={overrideByMarket[m.market_key]}
                topN={topN}
                autoFill={autoFill}
                busy={busy}
                onSet={onSet}
                onClear={onClear}
              />
            </div>
          );
        })}
        {unranked.length > 0 && (
          <button type="button" className="rr-unranked-toggle" onClick={() => setShowUnranked((v) => !v)}>
            {showUnranked ? 'Hide' : 'Show'} {unranked.length} unranked market{unranked.length === 1 ? '' : 's'}
          </button>
        )}
        {showUnranked &&
          unranked.map((m) => {
            const reason = m.features?.unranked_reason;
            const why = UNRANKED[reason] || { label: reason || 'Unranked', cls: 'rr-tag-gray', hint: '' };
            const overs = m.features?.market_over_bets || 0;
            const ov = overrideByMarket[m.market_key];
            return (
              <div key={m.market_key} className="rr-market is-unranked" title={why.hint}>
                <span className="rr-score rr-score-none">no rank</span>
                <span className="rr-market-name">
                  {marketLabel(m.market_id)}
                  <span className={`rr-tag ${why.cls}`}>{why.label}</span>
                </span>
                <span className="rr-overs" title={`${m.features?.market_bets ?? 0} entries, ${overs} on the over`}>
                  <span className="rr-overs-bar">
                    <span style={{ width: `${(overs / maxOvers) * 100}%` }} />
                  </span>
                  <span className="rr-dim">{overs} over</span>
                </span>
                {m.features?.unranked_reason === 'not_quoted' && !ov ? (
                  <span />
                ) : (
                  <MarketActions
                    marketKey={m.market_key}
                    override={ov}
                    topN={topN}
                    autoFill={autoFill}
                    busy={busy}
                    onSet={onSet}
                    onClear={onClear}
                  />
                )}
              </div>
            );
          })}
      </div>
      )}
    </div>
  );
}

function OverridesPanel({ overrides, fixturesById, autoFill, busy, onClear }) {
  return (
    <aside className="rr-panel rr-side">
      <div className="rr-panel-head">
        <h2>Overrides</h2>
        <span className="rr-count">{overrides.length}</span>
      </div>
      <dl className="rr-explain">
        <div>
          <dt><span className="rr-dot rr-dot-amber" />Pin</dt>
          <dd>
            {autoFill
              ? 'Holds a player in a slot, that market first. Survives every recompute.'
              : 'Features one market in a slot. Only pinned markets are featured.'}
          </dd>
        </div>
        {autoFill && (
          <div>
            <dt><span className="rr-dot rr-dot-red" />Exclude</dt>
            <dd>Keeps one market out of Featured; the player’s other markets still rank.</dd>
          </div>
        )}
      </dl>
      {overrides.length === 0 ? (
        <p className="rr-empty-sm">
          {autoFill ? 'No overrides in this scope. The ranking is fully computed.' : 'No pins in this scope, so nothing is featured.'}
        </p>
      ) : (
        <ul className="rr-ov-list">
          {overrides.map((o) => {
            const [fid, player, mid] = o.market_key.split(':');
            return (
              <li key={o.id} className={o.excluded ? 'is-excl' : 'is-pin'}>
                <div className="rr-ov-main">
                  <span className={`rr-tag ${o.excluded ? 'rr-tag-red' : 'rr-tag-amber'}`}>
                    {o.excluded ? 'Excluded' : `Pin #${o.pin_slot}`}
                  </span>
                  <span className="rr-ov-target">
                    {player.replace(/_/g, ' ')} · {marketLabel(mid)}
                  </span>
                </div>
                <div className="rr-ov-meta">
                  {o.scope_type === 'league' && <span>{matchup(fixturesById[fid])}</span>}
                  {o.reason && <span>“{o.reason}”</span>}
                  <span>{o.set_by}</span>
                  <span>until {kickoff(o.expires_at)}</span>
                </div>
                <button type="button" className="rr-btn rr-btn-ghost rr-btn-sm" disabled={busy} onClick={() => onClear(o.id)}>
                  Clear
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </aside>
  );
}

function CandidatesDrawer({ league, scopeType, scopeId, topN, autoFill, busy, onSet, onClear, onClose }) {
  const { data, isLoading } = useRiskRankCandidates(league, scopeType, scopeId);
  const [q, setQ] = useState('');
  const [approvedOnly, setApprovedOnly] = useState(false);
  const rows = useMemo(() => {
    const term = q.trim().toLowerCase();
    return (data?.candidates || []).filter(
      (c) =>
        (!approvedOnly || c.approved) &&
        (!term || `${c.player_name || c.player_key} ${marketLabel(c.market_id)}`.toLowerCase().includes(term))
    );
  }, [data, q, approvedOnly]);
  return (
    <div className="rr-drawer-backdrop" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div className="rr-drawer" role="dialog" aria-label="All quotable markets">
        <div className="rr-panel-head">
          <div>
            <h2>All quotable markets</h2>
            <p className="rr-sub">Pin any market into Featured, approved or not. Sorted by player entries, then fewest over entries.</p>
          </div>
          <button type="button" className="rr-btn rr-btn-ghost" onClick={onClose} aria-label="Close">
            ✕
          </button>
        </div>
        <div className="rr-drawer-tools">
          <input className="rr-input" placeholder="Search player or market" value={q} onChange={(e) => setQ(e.target.value)} />
          <label className="rr-check">
            <input type="checkbox" checked={approvedOnly} onChange={(e) => setApprovedOnly(e.target.checked)} />
            Approved only
          </label>
        </div>
        {isLoading ? (
          <div className="rr-skeleton" />
        ) : (
          <div className="rr-table-wrap">
            <table className="rr-table">
              <thead>
                <tr>
                  <th>Player</th>
                  <th>Market</th>
                  <th className="num">Player entries</th>
                  <th className="num">Over entries</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {rows.slice(0, 300).map((c) => (
                  <tr key={c.market_key}>
                    <td>
                      {c.player_name || c.player_key} {c.position && <span className="rr-pos">{c.position}</span>}
                    </td>
                    <td>
                      {marketLabel(c.market_id)} {!c.approved && <span className="rr-tag rr-tag-gray">Not approved</span>}
                    </td>
                    <td className="num">{c.player_total_bets.toLocaleString()}</td>
                    <td className="num">{c.market_over_bets.toLocaleString()}</td>
                    <td className="right">
                      <MarketActions marketKey={c.market_key} override={c.override} topN={topN} autoFill={autoFill} busy={busy} onSet={onSet} onClear={onClear} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {rows.length > 300 && <p className="rr-empty-sm">Showing 300 of {rows.length}. Narrow the search.</p>}
          </div>
        )}
      </div>
    </div>
  );
}

export default function RiskRankPage() {
  const { user } = useAuth();
  const { data: cfg } = useRiskRankConfig();
  const leagues = useMemo(() => Object.keys(cfg?.leagues || {}), [cfg]);
  const topN = cfg?.top_n ?? 10;
  const autoFill = cfg?.auto_fill ?? true;
  const [league, setLeague] = useState('');
  const [scopeKey, setScopeKey] = useState('league');
  const [showAll, setShowAll] = useState(false);
  const [collapsedByDefault, setCollapsedByDefault] = useState(readCollapsedDefault);
  const [collapseExceptions, setCollapseExceptions] = useState(() => new Set());
  const setAllCollapsed = (value) => {
    setCollapsedByDefault(value);
    writeCollapsedDefault(value);
    setCollapseExceptions(new Set());
  };
  const togglePlayer = (key) =>
    setCollapseExceptions((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  const [toast, setToast] = useState(null);

  useEffect(() => {
    if (!league && leagues.length) setLeague(leagues[0]);
  }, [league, leagues]);
  useEffect(() => {
    if (!toast) return undefined;
    const t = setTimeout(() => setToast(null), 5000);
    return () => clearTimeout(t);
  }, [toast]);

  const { data: board, isLoading } = useRiskRankBoard(league);
  const setOverride = useSetRiskRankOverride();
  const clearOverride = useClearRiskRankOverride();
  const recompute = useRecomputeRiskRank();
  const busy = setOverride.isPending || clearOverride.isPending || recompute.isPending;

  const scopeType = scopeKey === 'league' ? 'league' : 'fixture';
  const scopeId = scopeKey === 'league' ? league : scopeKey;
  const fixtures = useMemo(() => board?.fixtures || [], [board]);
  const fixturesById = useMemo(() => Object.fromEntries(fixtures.map((f) => [f.fixture_id, f])), [fixtures]);
  const allRows = useMemo(() => board?.rows || [], [board]);

  const playersInScope = useMemo(() => {
    const byPlayer = new Map();
    allRows
      .filter((r) => r.scope_type === scopeType && r.scope_id === scopeId)
      .sort((a, b) => a.player_rank - b.player_rank || (b.score ?? -1) - (a.score ?? -1))
      .forEach((r) => {
        const k = `${r.fixture_id}:${r.player_key}`;
        if (!byPlayer.has(k)) byPlayer.set(k, { key: k, rank: r.player_rank, markets: [] });
        byPlayer.get(k).markets.push(r);
      });
    return [...byPlayer.values()];
  }, [allRows, scopeType, scopeId]);

  const featuredCount = useMemo(() => {
    const out = {};
    allRows.forEach((r) => {
      if (r.scope_type === 'fixture' && r.market_rank === 1) out[r.scope_id] = (out[r.scope_id] || 0) + 1;
    });
    return out;
  }, [allRows]);
  const leagueCount = useMemo(
    () => allRows.filter((r) => r.scope_type === 'league' && r.market_rank === 1).length,
    [allRows]
  );

  const overrides = useMemo(
    () => (board?.overrides || []).filter((o) => o.scope_type === scopeType && o.scope_id === scopeId),
    [board, scopeType, scopeId]
  );
  const overrideByMarket = useMemo(() => Object.fromEntries(overrides.map((o) => [o.market_key, o])), [overrides]);
  const computedAt = allRows[0]?.computed_at;
  const nextIn = autoFill && computedAt && cfg?.interval_s
    ? Math.max(0, Math.round((new Date(computedAt).getTime() + cfg.interval_s * 1000 - Date.now()) / 60000))
    : null;

  const onSet = async (marketKey, value, reason) => {
    try {
      await setOverride.mutateAsync({
        league,
        body: {
          scope_type: scopeType,
          scope_id: scopeId,
          market_key: marketKey,
          reason: reason.trim() || null,
          set_by: user?.email || 'dashboard',
          ...value,
        },
      });
      setToast({ kind: 'ok', text: value.excluded ? 'Market excluded. Ranking recomputed.' : `Pinned to #${value.pin_slot}. Ranking recomputed.` });
      return true;
    } catch (e) {
      setToast({ kind: 'err', text: formatApiError(e) });
      return false;
    }
  };
  const onClear = async (id) => {
    try {
      await clearOverride.mutateAsync({ league, id });
      setToast({ kind: 'ok', text: 'Override cleared. Ranking recomputed.' });
    } catch (e) {
      setToast({ kind: 'err', text: formatApiError(e) });
    }
  };
  const onRecompute = async () => {
    try {
      const s = await recompute.mutateAsync(league);
      setToast({ kind: 'ok', text: `Recomputed: ${s.rows} ranked markets across ${s.fixtures} fixtures.` });
    } catch (e) {
      setToast({ kind: 'err', text: formatApiError(e) });
    }
  };

  const scopeFixture = scopeType === 'fixture' ? fixturesById[scopeId] : null;

  return (
    <div className="rr-root">
      <header className="rr-head">
        <div>
          <div className="rr-eyebrow">Operations · Hot pill</div>
          <h1>Risk Rank</h1>
          {autoFill ? (
            <p className="rr-lede">
              What the Hot pill features. Popularity picks the top {topN} players; each player&apos;s
              approved markets come first, then the rest, fewest over entries first, so exposure spreads instead of piling onto one
              leg. Pin or exclude to steer it.
            </p>
          ) : (
            <p className="rr-lede">
              What the Hot pill features. Pins only: each pinned market is featured in its slot, and nothing else is ranked,
              so the app shows its own popular legs wherever nothing is pinned. Pin from &ldquo;All markets&rdquo;.
            </p>
          )}
        </div>
        <div className="rr-head-side">
          <div className="rr-fresh">
            <span className="rr-live-dot" />
            {computedAt ? `Updated ${ago(computedAt)}` : 'Not computed yet'}
            {nextIn !== null && <span className="rr-dim"> · next in {nextIn}m</span>}
          </div>
          <button type="button" className="rr-btn rr-btn-ghost" disabled={busy || !league} onClick={onRecompute}>
            {recompute.isPending ? 'Recomputing…' : 'Recompute now'}
          </button>
        </div>
      </header>

      <div className="rr-filters">
        <span className="rr-filter-label">League</span>
        <div className="rr-seg" role="group" aria-label="League">
          {leagues.map((l) => (
            <button
              key={l}
              type="button"
              className={league === l ? 'is-on' : ''}
              aria-pressed={league === l}
              onClick={() => {
                setLeague(l);
                setScopeKey('league');
              }}
            >
              {l.toUpperCase()}
            </button>
          ))}
        </div>
      </div>
      <nav className="rr-scopes" aria-label="Scope">
        <button type="button" className={`rr-scope ${scopeKey === 'league' ? 'is-on' : ''}`} onClick={() => setScopeKey('league')}>
          <span className="rr-scope-title">{(league || '').toUpperCase()} Hot pill</span>
          <span className="rr-scope-sub">All pregame games · {leagueCount} featured</span>
        </button>
        <span className="rr-scopes-sep" />
        {fixtures.map((f) => (
          <button
            key={f.fixture_id}
            type="button"
            className={`rr-scope ${scopeKey === f.fixture_id ? 'is-on' : ''}`}
            onClick={() => setScopeKey(f.fixture_id)}
          >
            <span className="rr-scope-title">
              {matchup(f)} {f.status === 'live' && <span className="rr-tag rr-tag-live">Live</span>}
            </span>
            <span className="rr-scope-sub">{kickoff(f.start_time_utc)} · {featuredCount[f.fixture_id] || 0} featured</span>
          </button>
        ))}
      </nav>

      <div className="rr-grid">
        <section className="rr-panel">
          <div className="rr-panel-head">
            <div>
              <h2>Featured {scopeType === 'league' ? `· ${(league || '').toUpperCase()}` : `· ${matchup(scopeFixture)}`}</h2>
              <p className="rr-sub">
                {scopeType === 'league'
                  ? 'Ranked across every game that has not kicked off. In-play markets drop out automatically.'
                  : 'Ranked within this game, pregame and live.'}
              </p>
            </div>
            <div className="rr-head-actions">
              <div className="rr-seg" role="group" aria-label="Player cards">
                <button type="button" className={!collapsedByDefault && collapseExceptions.size === 0 ? 'is-on' : ''} onClick={() => setAllCollapsed(false)}>
                  Expand all
                </button>
                <button type="button" className={collapsedByDefault && collapseExceptions.size === 0 ? 'is-on' : ''} onClick={() => setAllCollapsed(true)}>
                  Collapse all
                </button>
              </div>
              <button type="button" className="rr-btn rr-btn-blue" onClick={() => setShowAll(true)}>
                All markets
              </button>
            </div>
          </div>
          <div className="rr-legend">
            <span><span className="rr-dot rr-dot-blue" />Computed</span>
            <span><span className="rr-dot rr-dot-amber" />Pinned</span>
            <span><span className="rr-dot rr-dot-red" />Excluded</span>
            <span className="rr-legend-score">
              Score <Score value={scopeType === 'league' ? 0.99899 : 0.9899} scopeType={scopeType} />
              <span className="rr-dim">= player #2, market #1</span>
            </span>
          </div>
          {isLoading ? (
            <div className="rr-skeleton" />
          ) : playersInScope.length === 0 ? (
            <div className="rr-empty">
              <div className="rr-empty-title">Nothing featured here yet</div>
              <p>
                {autoFill
                  ? 'No player in this scope has entries on a quotable approved market. Pin a market from “All markets” to feature one anyway.'
                  : 'Nothing is pinned in this scope. Pin a market from “All markets” to feature it.'}
              </p>
            </div>
          ) : (
            <div className="rr-players">
              {playersInScope.map((p) => (
                <PlayerRow
                  key={p.key}
                  player={p}
                  scopeType={scopeType}
                  fixturesById={fixturesById}
                  overrideByMarket={overrideByMarket}
                  topN={topN}
                  autoFill={autoFill}
                  busy={busy}
                  onSet={onSet}
                  onClear={onClear}
                  collapsed={collapsedByDefault !== collapseExceptions.has(p.key)}
                  onToggle={() => togglePlayer(p.key)}
                />
              ))}
            </div>
          )}
        </section>
        <OverridesPanel overrides={overrides} fixturesById={fixturesById} autoFill={autoFill} busy={busy} onClear={onClear} />
      </div>

      {showAll && (
        <CandidatesDrawer
          league={league}
          scopeType={scopeType}
          scopeId={scopeId}
          topN={topN}
          autoFill={autoFill}
          busy={busy}
          onSet={onSet}
          onClear={onClear}
          onClose={() => setShowAll(false)}
        />
      )}
      {toast && <div className={`rr-toast ${toast.kind === 'err' ? 'is-err' : ''}`} role="status">{toast.text}</div>}
    </div>
  );
}
